import { CoreArchiveDialog } from "@/components/core-archive-dialog";
import { useState, useEffect, useMemo } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useRoute, useLocation } from "wouter";
import { Plus, Edit } from "lucide-react";
import type { Account, Deal, ClientGroup } from "@shared/schema";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { AccountFormModal } from "@/components/account-form-modal";
import { DealFormModal } from "@/components/deal-form-modal";
import { LinkedDealsTable } from "@/components/linked-deals-table";
import { InlineTextField, InlineSelectField, InlineTextareaField, InlineDateField } from "@/components/inline-edit";
import { DetailPageShell } from "@/components/layout/detail-page-shell";
import { EntitySpecificFields } from "@/components/accounts/entity-specific-fields";
import { DirectorsPanel } from "@/components/accounts/directors-panel";
import { ShareholdersPanel } from "@/components/accounts/shareholders-panel";
import { PartnersPanel } from "@/components/accounts/partners-panel";
import { ProprietorPanel } from "@/components/accounts/proprietor-panel";
import { TrusteesPanel } from "@/components/accounts/trustees-panel";
import { BeneficialOwnersPanel } from "@/components/accounts/beneficial-owners-panel";
import { TrustBeneficiariesPanel } from "@/components/accounts/trust-beneficiaries-panel";
import { SmsfMembersPanel } from "@/components/accounts/smsf-members-panel";
import { RelatedEntitiesPanel } from "@/components/accounts/related-entities-panel";
import { ActivityFeed } from "@/components/activity/activity-feed";
import { AddressPanel } from "@/components/address-panel";
import { EntityDocumentsPanel } from "@/components/documents/entity-documents-panel";
import { useToast } from "@/hooks/use-toast";
import { useTabState } from "@/hooks/use-tab-state";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { buildNavigationUrl, buildBreadcrumbsFromUrl } from "@/lib/breadcrumbs";
import { formatABN } from "@/lib/utils";
import { FinancialsTabSidebarLayout } from "@/components/financial/layouts/financials-tab-sidebar-layout";
import { AccountCommitmentsPanel } from "@/components/financial/panels/account-commitments-panel";
import { useEntityRecord, useFinancialPeriods } from "@/hooks/use-financial-data";

const ENTITY_TYPE_COLORS = {
  company: "bg-blue-500/10 text-blue-700 dark:text-blue-400",
  trust: "bg-purple-500/10 text-purple-700 dark:text-purple-400",
  partnership: "bg-green-500/10 text-green-700 dark:text-green-400",
  sole_trader: "bg-orange-500/10 text-orange-700 dark:text-orange-400",
};

const ENTITY_TYPE_OPTIONS = [
  { value: "company", label: "Company" },
  { value: "trust", label: "Trust" },
  { value: "partnership", label: "Partnership" },
  { value: "sole_trader", label: "Sole Trader" },
];

export default function AccountDetail() {
  // Match both /accounts/:id and /accounts/:id/financials routes
  const [_matchStandard, paramsStandard] = useRoute("/accounts/:id");
  const [matchFinancials, paramsFinancials] = useRoute("/accounts/:id/financials");
  const params = paramsStandard || paramsFinancials;

  // Determine if we're on the /financials sub-route
  const isFinancialsRoute = Boolean(matchFinancials);

  const [_location, navigate] = useLocation();
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDealModalOpen, setIsDealModalOpen] = useState(false);
  const [isArchiveDialogOpen, setIsArchiveDialogOpen] = useState(false);

  // Use URL hash for tab persistence, default to financials if on /financials route
  const defaultTab = isFinancialsRoute ? "financials" : "overview";
  const [activeTab, setActiveTab] = useTabState(defaultTab);

  const { toast } = useToast();

  const { data: account, isLoading } = useQuery<Account>({
    queryKey: ["/api/crm/accounts", params?.id],
    enabled: !!params?.id,
  });

  // Fetch deals linked to this account
  const { data: deals = [] } = useQuery<Deal[]>({
    queryKey: ['/api/crm/deals', { applicantId: params?.id, applicantType: 'account' }],
    enabled: !!params?.id,
  });

  // Fetch client group for this account
  const { data: clientGroup } = useQuery<ClientGroup>({
    queryKey: ["/api/crm/client-groups", account?.clientGroupId],
    enabled: !!account?.clientGroupId,
  });

  // Financial data hooks
  const { data: entityRecord, isPending: isEntityRecordPending, isLoadingError: isEntityRecordError } = useEntityRecord(params?.id);
  const [selectedPeriod, setSelectedPeriod] = useState<any>(undefined);

  // Reset selected period when account changes
  useEffect(() => {
    setSelectedPeriod(undefined);
  }, [params?.id]);

  // Fetch periods
  const { data: financialPeriods = [], isPending: isFinancialPeriodsPending, isLoadingError: isFinancialPeriodsError } = useFinancialPeriods(entityRecord?.id);

  // Auto-select first period when periods load (React Query v5 pattern)
  useEffect(() => {
    if (financialPeriods.length > 0 && !selectedPeriod) {
      console.log('Auto-selecting period (React Query v5 pattern):', financialPeriods[0]);
      setSelectedPeriod(financialPeriods[0]);
    }
  }, [financialPeriods, selectedPeriod]);

  useEffect(() => {
    if (!selectedPeriod) return;
    if (financialPeriods.length === 0) {
      setSelectedPeriod(undefined);
      return;
    }

    const nextSelectedPeriod = financialPeriods.find((period) => period.id === selectedPeriod.id);
    if (!nextSelectedPeriod) {
      setSelectedPeriod(financialPeriods[0]);
    }
  }, [financialPeriods, selectedPeriod]);

  // Count linked contacts for LINKED section - from account-people table (directors, beneficial owners, trustees, etc.)
  const { data: accountPeopleData } = useQuery<any[]>({
    queryKey: ["/api/crm/accounts", params?.id, "people"],
    enabled: !!params?.id,
  });

  // Also fetch shareholders for complete contact count
  const { data: shareholdersData } = useQuery<any[]>({
    queryKey: ["/api/crm/accounts", params?.id, "shareholders"],
    enabled: !!params?.id && account?.entityType === "company",
  });

  // Count unique contacts across both tables
  const contactsCount = useMemo(() => {
    const contactIds = new Set<string>();
    accountPeopleData?.forEach((person: any) => {
      if (person.contactId) contactIds.add(person.contactId);
    });
    shareholdersData?.forEach((shareholder: any) => {
      if (shareholder.contactId) contactIds.add(shareholder.contactId);
    });
    return contactIds.size;
  }, [accountPeopleData, shareholdersData]);

  const accountSourceFrom = useMemo(() => ({
    from: 'account' as const,
    fromId: account?.id ?? '',
    fromLabel: account?.legalName ?? '',
  }), [account?.id, account?.legalName]);

  const updateMutation = useMutation({
    mutationFn: async (updates: Partial<Account>) => {
      const response = await apiRequest("PATCH", `/api/crm/accounts/${params?.id}`, updates);
      return response;
    },
    onMutate: async (updates) => {
      await queryClient.cancelQueries({ queryKey: ["/api/crm/accounts", params?.id] });
      const previousAccount = queryClient.getQueryData(["/api/crm/accounts", params?.id]);
      if (previousAccount) {
        queryClient.setQueryData(["/api/crm/accounts", params?.id], {
          ...previousAccount,
          ...updates,
        });
      }
      return { previousAccount };
    },
    onError: (error: Error, _variables, context) => {
      if (context?.previousAccount) {
        queryClient.setQueryData(["/api/crm/accounts", params?.id], context.previousAccount);
      }
      toast({
        title: "Error",
        description: error.message || "Failed to save changes",
        variant: "destructive",
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/crm/accounts"] });
      queryClient.invalidateQueries({ queryKey: ["/api/crm/accounts", params?.id] });
    },
  });

  const handleFieldUpdate = async (field: keyof Account, value: any): Promise<void> => {
    await updateMutation.mutateAsync({ [field]: value });
  };





  const handleNavItemClick = (href: string) => {
    // Extract tab value from href (e.g., "#overview" -> "overview")
    const tabValue = href.replace("#", "");
    setActiveTab(tabValue);
  };

  if (isLoading) {
    return (
      <div className="flex h-screen w-full items-center justify-center">
        <p className="text-muted-foreground text-sm">Loading account...</p>
      </div>
    );
  }

  if (!account) {
    return (
      <div className="flex h-screen w-full items-center justify-center">
        <div className="text-center">
          <p className="text-muted-foreground mb-3 text-sm">The account you're looking for doesn't exist.</p>
          <Button onClick={() => navigate("/accounts")} size="sm">Back to Accounts</Button>
        </div>
      </div>
    );
  }

  // Parse search parameters for dynamic breadcrumbs
  // ISSUE-065: Use window.location.search instead of wouter's location (which only contains pathname)
  const searchParams = new URLSearchParams(window.location.search);

  // Build dynamic breadcrumbs based on navigation history
  const breadcrumbs = buildBreadcrumbsFromUrl(
    searchParams,
    [
      { label: "Home", href: "/" },
      { label: "Accounts", href: "/accounts" },
      { label: account.legalName },
    ]
  );

  return (
    <>
    <DetailPageShell
      title={account.legalName}
      breadcrumbs={breadcrumbs}
      value={activeTab}
      onValueChange={setActiveTab}
      onNavItemClick={handleNavItemClick}
      badge={
        <>
        <Badge variant="secondary" className={`capitalize text-xs ${ENTITY_TYPE_COLORS[account.entityType as keyof typeof ENTITY_TYPE_COLORS]}`}>
          {account.entityType.replace("_", " ")}
        </Badge>
        {account.archivedAt && <> <span role="status" aria-label="Record status">Archived</span></>}
        </>
      }
      navItems={[
        { label: "Overview", href: "#overview" },
        { label: "Structure", href: "#structure" },
        { label: "Financials", href: "#financials" },
        { label: "Deals", href: "#deals", count: deals.length },
        { label: "Related Entities", href: "#related-entities" },
        { label: "Documents", href: "#documents" },
        { label: "Communications", href: "#communications" },
        { label: "Activity", href: "#activity" },
      ]}
      linkedEntities={[
        { label: "Contacts", count: contactsCount, href: "#structure" },
        { label: "Deals", count: deals.length, href: "#deals" },
        { label: "Documents", count: 0, href: "#documents" },
      ]}
      tabs={[
        {
          value: "overview",
          label: "Overview",
          content: (
            <div id="overview" className="space-y-2">
              {/* Account Details - Consolidated per PRD Section 4.4 */}
              <Card>
                <CardContent className="pt-2">
                  <h3 className="text-xs font-semibold text-muted-foreground mb-1.5">ACCOUNT DETAILS</h3>
                  <div className="grid grid-cols-2 gap-2">
                    <InlineTextField
                      value={account.legalName}
                      onSave={(value) => handleFieldUpdate("legalName", value)}
                      label="Legal Name"
                      placeholder="Enter legal name"
                      testId="input-legal-name"
                    />
                    <InlineTextField
                      value={account.tradingName}
                      onSave={(value) => handleFieldUpdate("tradingName", value || null)}
                      label="Trading Name"
                      placeholder="Optional"
                      testId="input-trading-name"
                    />
                    <InlineSelectField
                      value={account.entityType}
                      options={ENTITY_TYPE_OPTIONS}
                      onSave={(value) => handleFieldUpdate("entityType", value)}
                      label="Entity Type"
                      placeholder="Select type"
                      testId="select-entity-type"
                    />
                    <InlineTextField
                      value={account.industry}
                      onSave={(value) => handleFieldUpdate("industry", value || null)}
                      label="Industry"
                      placeholder="Optional"
                      testId="input-industry"
                    />
                    <InlineTextField
                      value={account.abn}
                      onSave={(value) => handleFieldUpdate("abn", value || null)}
                      label="ABN"
                      placeholder="Enter ABN"
                      testId="input-abn"
                      formatDisplay={(val) => formatABN(val) || "-"}
                    />
                    {account.entityType === "company" && (
                      <InlineTextField
                        value={account.acn}
                        onSave={(value) => handleFieldUpdate("acn", value || null)}
                        label="ACN"
                        placeholder="Enter ACN"
                        testId="input-acn"
                      />
                    )}
                    <InlineTextField
                      value={account.anzsicCode}
                      onSave={(value) => handleFieldUpdate("anzsicCode", value || null)}
                      label="ANZSIC Code"
                      placeholder="Enter ANZSIC code"
                      testId="input-anzsic-code"
                    />
                  </div>
                </CardContent>
              </Card>

              {/* Registration & GST - Consolidated from Registration Dates section */}
              <Card>
                <CardContent className="pt-2">
                  <h3 className="text-xs font-semibold text-muted-foreground mb-1.5">REGISTRATION</h3>
                  <div className="grid grid-cols-2 gap-2">
                    <InlineDateField
                      value={account.abnRegistrationDate}
                      onSave={(value) => handleFieldUpdate("abnRegistrationDate", value)}
                      label="ABN Registration Date"
                      placeholder="Select date"
                      testId="input-abn-registration-date"
                    />
                    {account.entityType === "company" && (
                      <InlineDateField
                        value={account.acnStartDate}
                        onSave={(value) => handleFieldUpdate("acnStartDate", value)}
                        label="ACN Start Date"
                        placeholder="Select date"
                        testId="input-acn-start-date"
                      />
                    )}
                    <InlineDateField
                      value={account.gstRegistrationDate}
                      onSave={(value) => handleFieldUpdate("gstRegistrationDate", value)}
                      label="GST Registration Date"
                      placeholder="Select date"
                      testId="input-gst-registration-date"
                    />
                  </div>
                </CardContent>
              </Card>

              {/* Entity-Specific Fields */}
              <Card>
                <CardContent className="pt-2">
                  <h3 className="text-xs font-semibold text-muted-foreground mb-1.5">
                    {account.entityType === "company" ? "COMPANY DETAILS" :
                     account.entityType === "trust" ? "TRUST DETAILS" :
                     account.entityType === "partnership" ? "PARTNERSHIP DETAILS" : "ENTITY DETAILS"}
                  </h3>
                  <EntitySpecificFields account={account} onFieldUpdate={handleFieldUpdate} />
                </CardContent>
              </Card>

              {/* Business Profile */}
              <Card>
                <CardContent className="pt-2">
                  <h3 className="text-xs font-semibold text-muted-foreground mb-1.5">BUSINESS PROFILE</h3>
                  <div className="grid grid-cols-2 gap-2">
                    <InlineTextField
                      value={account.annualRevenue ? String(account.annualRevenue) : ""}
                      onSave={(value) => handleFieldUpdate("annualRevenue", value ? value : null)}
                      label="Annual Revenue"
                      placeholder="Enter annual revenue"
                      testId="input-annual-revenue"
                      formatDisplay={(val) =>
                        val
                          ? new Intl.NumberFormat("en-AU", {
                              style: "currency",
                              currency: "AUD",
                              minimumFractionDigits: 0,
                              maximumFractionDigits: 0,
                            }).format(Number(val))
                          : "-"
                      }
                    />
                    <InlineTextField
                      value={account.employeeCount ? String(account.employeeCount) : ""}
                      onSave={(value) => handleFieldUpdate("employeeCount", value ? parseInt(value, 10) : null)}
                      label="Employee Count"
                      placeholder="Enter employee count"
                      testId="input-employee-count"
                    />
                  </div>
                </CardContent>
              </Card>

              {/* Contact Information */}
              <Card>
                <CardContent className="pt-2">
                  <h3 className="text-xs font-semibold text-muted-foreground mb-1.5">CONTACT INFORMATION</h3>
                  <div className="grid grid-cols-2 gap-2">
                    <InlineTextField
                      value={account.email}
                      onSave={(value) => handleFieldUpdate("email", value || null)}
                      label="Email"
                      placeholder="Optional"
                      testId="input-email"
                    />
                    <InlineTextField
                      value={account.phone}
                      onSave={(value) => handleFieldUpdate("phone", value || null)}
                      label="Phone"
                      placeholder="Optional"
                      testId="input-phone"
                    />
                    <InlineTextField
                      value={account.website}
                      onSave={(value) => handleFieldUpdate("website", value || null)}
                      label="Website"
                      placeholder="Enter website URL"
                      testId="input-website"
                      formatDisplay={(val) =>
                        val ? (
                          <a
                            href={val.startsWith("http") ? val : `https://${val}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-primary hover:underline"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {val}
                          </a>
                        ) : (
                          "-"
                        )
                      }
                    />
                    <InlineTextField
                      value={account.linkedin}
                      onSave={(value) => handleFieldUpdate("linkedin", value || null)}
                      label="LinkedIn"
                      placeholder="Enter LinkedIn URL"
                      testId="input-linkedin"
                      formatDisplay={(val) =>
                        val ? (
                          <a
                            href={val.startsWith("http") ? val : `https://${val}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-primary hover:underline"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {val}
                          </a>
                        ) : (
                          "-"
                        )
                      }
                    />
                  </div>
                </CardContent>
              </Card>

              {/* Notes */}
              <Card>
                <CardContent className="pt-2">
                  <h3 className="text-xs font-semibold text-muted-foreground mb-1.5">NOTES</h3>
                  <InlineTextareaField
                    value={account.notes}
                    onSave={(value) => handleFieldUpdate("notes", value || null)}
                    label="Notes"
                    placeholder="Enter notes (optional)"
                    rows={4}
                    testId="input-notes"
                  />
                </CardContent>
              </Card>
            </div>
          ),
        },
        {
          value: "structure",
          label: "Structure",
          content: (
            <div id="structure" className="space-y-2">
              {/* Directors - Only for Companies */}
              {account.entityType === "company" && (
                <DirectorsPanel account={account} />
              )}

              {/* Shareholders - Only for Companies */}
              {account.entityType === "company" && (
                <ShareholdersPanel account={account} />
              )}

              {/* Partners - Only for Partnerships */}
              {account.entityType === "partnership" && (
                <PartnersPanel account={account} />
              )}

              {/* Proprietor - Only for Sole Traders */}
              {account.entityType === "sole_trader" && (
                <ProprietorPanel account={account} />
              )}

              {/* Trustees - Only for Trusts */}
              {account.entityType === "trust" && (
                <TrusteesPanel account={account} />
              )}

              {/* Beneficiaries - Only for Trusts */}
              {account.entityType === "trust" && (
                <TrustBeneficiariesPanel account={account} />
              )}

              {/* SMSF Members - Only for SMSFs */}
              {account.entityType === "trust" && account.trustType === "smsf" && (
                <SmsfMembersPanel account={account} />
              )}

              {/* Beneficial Owners - For Companies, Trusts, and Partnerships */}
              {(account.entityType === "company" || account.entityType === "trust" || account.entityType === "partnership") && (
                <BeneficialOwnersPanel account={account} />
              )}

            </div>
          ),
        },
        {
          value: "addresses",
          label: "Addresses",
          content: (
            <div id="addresses" className="space-y-2">
              <Card>
                <CardContent className="pt-2">
                  <AddressPanel 
                    entityType="account" 
                    entityId={account.id}
                    data-testid="panel-account-addresses"
                  />
                </CardContent>
              </Card>
            </div>
          ),
        },
        {
          value: "financials",
          label: "Financials",
          content: (
            <div id="financials" className="space-y-4">
              <FinancialsTabSidebarLayout
                accountId={params?.id || ''}
                entityRecord={entityRecord}
                periods={financialPeriods}
                isLoading={isEntityRecordPending || (!!entityRecord?.id && isFinancialPeriodsPending)}
                isError={isEntityRecordError || isFinancialPeriodsError}
                selectedPeriod={selectedPeriod}
                onPeriodSelect={setSelectedPeriod}
                onRecordCreated={(_record) => {
                  // Refetch entity record and periods so new data appears without manual refresh
                  queryClient.refetchQueries({ queryKey: ['financial', 'entity-record', params?.id] });
                  queryClient.refetchQueries({ queryKey: ['financial', 'periods', entityRecord?.id] });
                  toast({
                    title: "Financial record created",
                    description: "You can now add financial periods and data",
                  });
                }}
              />
              <AccountCommitmentsPanel accountId={params?.id || ''} />
            </div>
          ),
        },
        {
          value: "deals",
          label: `Deals (${deals.length})`,
          content: (
            <div id="deals">
              <Card>
                <CardContent className="pt-2">
                  <LinkedDealsTable
                    applicantId={account.id}
                    applicantType="account"
                    sourceFrom={accountSourceFrom}
                  />
                </CardContent>
              </Card>
            </div>
          ),
        },
        {
          value: "documents",
          label: "Documents",
          content: (
            <div id="documents">
              <EntityDocumentsPanel entityId={account.id} entityType="account" />
            </div>
          ),
        },
        {
          value: "related-entities",
          label: "Related Entities",
          content: (
            <div id="related-entities">
              <RelatedEntitiesPanel
                accountId={account.id}
                entityType={account.entityType}
                trustType={account.trustType ?? undefined}
              />
            </div>
          ),
        },
        {
          value: "communications",
          label: "Communications",
          content: (
            <div id="communications">
              <ActivityFeed
                entityType="account"
                entityId={account.id}
                lens="email"
                testId="panel-account-communications"
              />
            </div>
          ),
        },
        {
          value: "activity",
          label: "Activity",
          content: (
            <div id="activity">
              <ActivityFeed
                entityType="account"
                entityId={account.id}
                testId="panel-account-activity"
              />
            </div>
          ),
        },
      ]}
      rightPanel={
        <div className="space-y-4">
          {/* Key Dates - MUST be first (metadata) */}
          <div>
            <h3 className="text-xs font-semibold text-muted-foreground mb-1.5">KEY DATES</h3>
            <div className="space-y-2 text-xs">
              <div>
                <p className="text-muted-foreground">Created</p>
                <p className="font-medium">
                  {account.createdAt
                    ? new Date(account.createdAt).toLocaleDateString("en-AU")
                    : "-"}
                </p>
              </div>
              <div>
                <p className="text-muted-foreground">Modified</p>
                <p className="font-medium">
                  {account.updatedAt
                    ? new Date(account.updatedAt).toLocaleDateString("en-AU")
                    : "-"}
                </p>
              </div>
            </div>
          </div>

          <div className="border-t pt-3">
            <h3 className="text-xs font-semibold text-muted-foreground mb-1.5">QUICK STATS</h3>
            <div className="space-y-2 text-xs">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Deals:</span>
                <span className="font-medium">{deals.length}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Contacts:</span>
                <span className="font-medium">{contactsCount}</span>
              </div>
            </div>
          </div>

          {/* Client Group */}
          <div className="border-t pt-3">
            <h3 className="text-xs font-semibold text-muted-foreground mb-1.5">CLIENT GROUP</h3>
            {clientGroup ? (
              <button
                onClick={() => {
                  const url = buildNavigationUrl(`/client-groups/${clientGroup.id}`, {
                    from: 'account',
                    fromId: params?.id || '',
                    fromLabel: account.legalName || 'Account',
                  });
                  navigate(url);
                }}
                className="text-sm text-primary hover:underline font-medium"
                data-testid="link-client-group"
              >
                {clientGroup.groupName}
              </button>
            ) : (
              <p className="text-sm text-muted-foreground">-</p>
            )}
          </div>

          {/* Actions */}
          <div className="border-t pt-3">
            <h3 className="text-xs font-semibold text-muted-foreground mb-1.5">ACTIONS</h3>
            <div className="space-y-2">
              <Button
                size="sm"
                className="w-full justify-start text-xs"
                variant="outline"
                onClick={() => setIsEditModalOpen(true)}
                data-testid="button-edit-account"
              >
                <Edit className="h-3 w-3 mr-1" />
                Edit Account
              </Button>
              <Button
                size="sm"
                className="w-full justify-start text-xs"
                variant="outline"
                onClick={() => setIsDealModalOpen(true)}
                data-testid="button-new-deal"
              >
                <Plus className="h-3 w-3 mr-1" />
                New Deal
              </Button>
            </div>
          </div>
        </div>
      }
      actions={
          <>
            <Button variant="outline" size="sm" onClick={() => setIsArchiveDialogOpen(true)}>
              {account.archivedAt ? "Restore Account" : "Archive Account"}
            </Button>
        <div className="flex gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              if (sessionStorage.getItem('cameFromApp') === 'true') {
                window.history.back();
              } else {
                navigate("/accounts");
              }
            }}
            data-testid="button-back"
          >
            Back to List
          </Button>
        </div>
          </>
      }
    />

    <AccountFormModal
      open={isEditModalOpen}
      onOpenChange={setIsEditModalOpen}
      account={account}
    />

    <DealFormModal
        open={isDealModalOpen}
        onOpenChange={setIsDealModalOpen}
        applicantId={account.id}
        applicantType="account"
      />

    <CoreArchiveDialog entity="account" record={account} open={isArchiveDialogOpen}
      onOpenChange={setIsArchiveDialogOpen} />
    </>
  );
}
